import React, { useState } from 'react'
import { graphql, useStaticQuery } from 'gatsby'
import { Flex, Box } from '@rebass/grid'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import GradientMask from '../components/GradientMask'
import styled from 'styled-components'
import { ThemeConsumer } from 'styled-components'

// Icons
import QuickDrag from './icons/QuickDrag'
import ExportIcons from './icons/ExportIcons'
import IconSvgCleaner from './icons/IconSvgCleaner'

const StyledHighlightedFeature = styled(Box)`
  transition: opacity 0.15s ease-in-out;
  opacity: 0.65;
  cursor: pointer;
  will-change: opacity;
  transform: translateZ(0);
  backface-visibility: hidden;

  &:hover {
    opacity: 1;
  }

  &.active {
    opacity: 1;
  }
`

const StyledHighlightedImageWrapper = styled.div`
  opacity: 0;
  transition: all 0.4s ease-in-out;
  will-change: opacity;
  transform: translateZ(0);
  backface-visibility: hidden;

  &.active {
    opacity: 1;
  }
`

const MainHighlights = ({ props }) => {
  // Fetch images
  const data = useStaticQuery(graphql`
    query {
      dragDropScreenshot: file(relativePath: { eq: "dragdrop@2x.png" }) {
        childImageSharp {
          gatsbyImageData(
            width: 2302
            quality: 100
            placeholder: NONE
            layout: CONSTRAINED
            formats: [AUTO, WEBP]
          )
        }
      }
      dragDropScreenshotDark: file(
        relativePath: { eq: "dragdrop-dark@2x.png" }
      ) {
        childImageSharp {
          gatsbyImageData(
            width: 2302
            quality: 100
            placeholder: NONE
            layout: CONSTRAINED
            formats: [AUTO, WEBP]
          )
        }
      }
      exportScreenshot: file(relativePath: { eq: "export@2x.png" }) {
        childImageSharp {
          gatsbyImageData(
            width: 2302
            quality: 100
            placeholder: NONE
            layout: CONSTRAINED
            formats: [AUTO, WEBP]
          )
        }
      }
      exportScreenshotDark: file(relativePath: { eq: "export-dark@2x.png" }) {
        childImageSharp {
          gatsbyImageData(
            width: 2302
            quality: 100
            placeholder: NONE
            layout: CONSTRAINED
            formats: [AUTO, WEBP]
          )
        }
      }
      optimizerScreenshot: file(relativePath: { eq: "optimizer@2x.png" }) {
        childImageSharp {
          gatsbyImageData(
            width: 2302
            quality: 100
            placeholder: NONE
            layout: CONSTRAINED
            formats: [AUTO, WEBP]
          )
        }
      }
      optimizerScreenshotDark: file(
        relativePath: { eq: "optimizer-dark@2x.png" }
      ) {
        childImageSharp {
          gatsbyImageData(
            width: 2302
            quality: 100
            placeholder: NONE
            layout: CONSTRAINED
            formats: [AUTO, WEBP]
          )
        }
      }
    }
  `)

  // State which controls which image to show
  let [highlighted, setHighlighted] = useState('DragDrop')

  return (
    <ThemeConsumer>
      {(theme) => (
        <Flex flexWrap="wrap" mb={[4, 5]} alignItems="center">
          <Box width={[1, 1 / 2]} px={[0, 3]} mb={[4, 0]}>
            <GradientMask alignment="left">
              <StyledHighlightedImageWrapper
                className={highlighted === 'DragDrop' ? 'active' : ''}>
                <GatsbyImage
                  image={getImage(
                    theme.appearance === 'light'
                      ? data.dragDropScreenshot
                      : data.dragDropScreenshotDark,
                  )}
                  alt="IconJar app - inspector"
                />
              </StyledHighlightedImageWrapper>
              <StyledHighlightedImageWrapper
                className={highlighted === 'Export' ? 'active' : ''}
                style={{ marginTop: '-71.57598499061913%' }}>
                <GatsbyImage
                  image={getImage(
                    theme.appearance === 'light'
                      ? data.exportScreenshot
                      : data.exportScreenshotDark,
                  )}
                  alt="IconJar app - inspector"
                />
              </StyledHighlightedImageWrapper>
              <StyledHighlightedImageWrapper
                className={highlighted === 'SVGOptimizer' ? 'active' : ''}
                style={{ marginTop: '-71.57598499061913%' }}>
                <GatsbyImage
                  image={getImage(
                    theme.appearance === 'light'
                      ? data.optimizerScreenshot
                      : data.optimizerScreenshotDark,
                  )}
                  alt="IconJar app - inspector"
                />
              </StyledHighlightedImageWrapper>
            </GradientMask>
          </Box>
          <Box width={[1, 1 / 2]} px={[0, 3]}>
            {/* Feature 1 (Drag + drop) */}
            <StyledHighlightedFeature
              mb={[4, 5]}
              className={highlighted === 'DragDrop' ? 'active' : ''}
              onMouseOver={() => setHighlighted('DragDrop')}>
              <Box mb={3}>
                <QuickDrag />
              </Box>
              <h4>Drag icons into your apps in any size or color</h4>
              <p>
                Use IconJar alongside your design tool, code editor, word
                processor or presentation software and streamline your
                workflows.
              </p>
              <p>
                For example: Google Apps, Figma/Sketch and Visual Studio Code.
              </p>
            </StyledHighlightedFeature>

            {/* Feature 2 (Export) */}
            <StyledHighlightedFeature
              mb={[4, 5]}
              className={highlighted === 'Export' ? 'active' : ''}
              onMouseOver={() => setHighlighted('Export')}>
              <Box mb={3}>
                <ExportIcons />
              </Box>
              <h4>Bulk export icons in the sizes you need</h4>
              <p>
                Save time by using one of our built-in presets for Android, iOS,
                macOS or create custom presets that suit your needs.
              </p>
            </StyledHighlightedFeature>

            {/* Feature 3 (SVG optimizer) */}
            <StyledHighlightedFeature
              mb={4}
              className={highlighted === 'SVGOptimizer' ? 'active' : ''}
              onMouseOver={() => setHighlighted('SVGOptimizer')}>
              <Box mb={3}>
                <IconSvgCleaner />
              </Box>
              <h4>Optimize SVGs and make them production ready</h4>
              <p>
                Use our SVG optimizer to make icons production-ready. We have
                plenty of settings for this too!
              </p>
            </StyledHighlightedFeature>
          </Box>
        </Flex>
      )}
    </ThemeConsumer>
  )
}

export default MainHighlights
